import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useShadowHost, useTopLevelPortal } from '@homerunner-next/widget-core/runtime'
import { useWidget } from '../../providers/widget'
import type { CardTier, TierPopoverConfig } from './tier-rating'

/**
 * Tier badge disclosure (#1821) — port of toolkit
 * `assets/js/tier-rating-frontend.js` (popover half). The trigger is a real
 * `<button aria-expanded aria-controls>` inside the card link (the favorites
 * heart is the precedent for a button inside the link): hover and focus open
 * a preview, click or tap pins it, Escape / outside click / blur / leave
 * close it; Tab from the badge lands on the popover link, Tab out hands focus
 * back. Viewport-aware: below the badge, flipped above when there is no room.
 *
 * The popover never renders inside the card. Under the production shadow-DOM
 * mount it portals to widget-core's elevated body portal (its own shadow root
 * at `document.body`, the widget's stylesheets cloned in, the host theme
 * mirrored — the dialog / sticky-bar precedent), so card `overflow` and hover
 * `transform` cannot clip it and its link is not nested in the card link.
 * Shadow-less mounts (plain-DOM dev shells, unit tests) portal to
 * `document.body` directly. Nothing portals on the server: the popover only
 * exists while open, and `open` starts false.
 *
 * One popover per page, as the toolkit's single shared element: opening a
 * badge closes whichever other badge is open.
 */
const HOVER_DELAY = 150
const LEAVE_DELAY = 120
const GAP = 8
export const TIER_POPOVER_LEARN_MORE = 'Learn about our ratings'

let closeActive: (() => void) | null = null

export function TierTrigger({ tier, popover, ariaLabel, children }: {
  tier: CardTier
  popover: TierPopoverConfig
  ariaLabel: string
  children: ReactNode
}) {
  const popId = useId()
  const titleId = `${popId}-title`
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  // Elevated portal target, created on the first open and kept for the
  // badge's lifetime (a per-open shadow root would re-clone the stylesheets
  // on every hover).
  const [armed, setArmed] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openRef = useRef(false)
  const pinnedRef = useRef(false)
  // close(true) hands focus back to the badge; the focus event that fires
  // must not reopen the popover it just closed.
  const suppressFocusOpen = useRef(false)
  openRef.current = open
  pinnedRef.current = pinned

  const { resolvedTheme } = useWidget()
  const shadowHost = useShadowHost()
  const elevated = useTopLevelPortal({ enabled: armed && !!shadowHost })

  const clearTimers = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    hoverTimer.current = null
    leaveTimer.current = null
  }

  const close = useCallback((returnFocus: boolean) => {
    clearTimers()
    if (closeActive === closeSelf.current) closeActive = null
    if (!openRef.current) return
    openRef.current = false
    pinnedRef.current = false
    setOpen(false)
    setPinned(false)
    if (returnFocus && triggerRef.current) {
      suppressFocusOpen.current = true
      try {
        triggerRef.current.focus()
      } finally {
        suppressFocusOpen.current = false
      }
    }
  }, [])
  const closeSelf = useRef<() => void>(() => close(false))
  closeSelf.current = () => close(false)

  const openPopover = (pin: boolean) => {
    clearTimers()
    if (closeActive && closeActive !== closeSelf.current) closeActive()
    closeActive = closeSelf.current
    openRef.current = true
    pinnedRef.current = pin
    setOpen(true)
    setPinned(pin)
    setArmed(true)
  }

  const focusInside = () => {
    const a = document.activeElement
    return !!(a && (a === triggerRef.current || popRef.current?.contains(a)))
  }

  const scheduleClose = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    leaveTimer.current = setTimeout(() => {
      // The pointer left, but keyboard focus is still on the badge or on the
      // popover link: that visitor is not done with it.
      if (openRef.current && !pinnedRef.current && !focusInside()) close(false)
    }, LEAVE_DELAY)
  }

  // Never navigate the card link, never reach the card's own click handlers.
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    if (openRef.current) {
      if (pinnedRef.current) close(false)
      else { pinnedRef.current = true; setPinned(true) } // a hover preview the visitor clicked stays open
      return
    }
    openPopover(true)
  }
  const onMouseEnter = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    if (openRef.current) return
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => openPopover(false), HOVER_DELAY)
  }
  const onMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    if (openRef.current && !pinnedRef.current) scheduleClose()
  }
  const onFocus = () => {
    if (suppressFocusOpen.current) return
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    if (!openRef.current) openPopover(false)
  }
  const onBlur = () => {
    if (!openRef.current || pinnedRef.current) return
    setTimeout(() => {
      if (!openRef.current) return
      const a = document.activeElement
      if (a && popRef.current?.contains(a)) return
      close(false)
    }, 0)
  }
  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Tab' && !e.shiftKey && openRef.current) {
      const link = popRef.current?.querySelector('a')
      if (link) { e.preventDefault(); link.focus() }
    }
  }

  // Document-level: outside click and Escape, only while open.
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: Event) => {
      const t = e.target as Node | null
      if (t && (popRef.current?.contains(t) || triggerRef.current?.contains(t))) return
      close(false)
    }
    const onDocKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(true) }
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onDocKey)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onDocKey)
    }
  }, [open, close])

  // Position below the badge, flipped above when there is no room, clamped
  // to the viewport; re-placed on scroll and resize while open.
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const trigger = triggerRef.current
      const pop = popRef.current
      if (!trigger || !pop) return
      const rect = trigger.getBoundingClientRect()
      const popRect = pop.getBoundingClientRect()
      const vw = window.innerWidth || document.documentElement.clientWidth || 0
      const vh = window.innerHeight || document.documentElement.clientHeight || 0
      const width = popRect.width || 0
      const height = popRect.height || 0
      const below = rect.bottom + GAP
      const above = rect.top - GAP - height
      const fitsBelow = !vh || below + height <= vh
      let top = fitsBelow || above < GAP ? below : above
      pop.classList.toggle('hrtk-tier-popover--above', top === above && !fitsBelow)
      // Neither side fits (short viewport, tall popover): keep it inside the
      // viewport anyway; the stylesheet caps its height and scrolls the rest.
      if (vh) top = Math.max(GAP, Math.min(top, vh - height - GAP))
      let left = rect.left + rect.width / 2 - width / 2
      left = vw ? Math.max(GAP, Math.min(left, vw - width - GAP)) : Math.max(GAP, left)
      pop.style.top = `${Math.round(top)}px`
      pop.style.left = `${Math.round(left)}px`
    }
    place()
    let raf = 0
    const onViewport = () => {
      if (raf) return
      raf = requestAnimationFrame(() => { raf = 0; place() })
    }
    window.addEventListener('resize', onViewport)
    window.addEventListener('scroll', onViewport, true)
    return () => {
      window.removeEventListener('resize', onViewport)
      window.removeEventListener('scroll', onViewport, true)
      if (raf) cancelAnimationFrame(raf)
    }
  // `elevated` too: under a shadow host the portal target (and so the popover
  // node) only exists one commit after the first open, so a `[open]`-only
  // effect placed nothing and the popover sat at the viewport corner until
  // the next scroll or resize (#1826).
  }, [open, elevated])

  useEffect(() => () => { clearTimers(); if (closeActive === closeSelf.current) closeActive = null }, [])

  // Under a shadow host the popover waits for the elevated target (one commit
  // after the first open) rather than flashing unstyled at body level.
  const target = shadowHost ? elevated : typeof document !== 'undefined' ? document.body : undefined
  const linkText = popover.linkText || TIER_POPOVER_LEARN_MORE

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="hrtk-tier__badge"
        aria-expanded={open}
        aria-controls={popId}
        aria-label={ariaLabel}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onTriggerKeyDown}
      >
        {children}
      </button>
      {open && target && createPortal(
        <div
          ref={popRef}
          id={popId}
          className={`hrtk-tier-popover hrtk-tier-popover--${tier.slug}`}
          role="group"
          aria-labelledby={titleId}
          data-hr-theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
          onMouseEnter={() => { if (leaveTimer.current) clearTimeout(leaveTimer.current) }}
          onMouseLeave={() => { if (openRef.current && !pinnedRef.current) scheduleClose() }}
          onKeyDown={(e) => { if (e.key === 'Tab') { e.preventDefault(); close(true) } }}
          onBlur={() => {
            if (!openRef.current || pinnedRef.current) return
            setTimeout(() => { if (openRef.current && !focusInside()) close(false) }, 0)
          }}
        >
          {popover.heading && <p className="hrtk-tier-popover__eyebrow">{popover.heading}</p>}
          <p className="hrtk-tier-popover__title" id={titleId}>{tier.label}</p>
          {tier.description.trim() && <p className="hrtk-tier-popover__desc">{tier.description.trim()}</p>}
          {popover.intro && <p className="hrtk-tier-popover__intro">{popover.intro}</p>}
          {popover.linkUrl && <a className="hrtk-tier-popover__link" href={popover.linkUrl}>{linkText}</a>}
        </div>,
        target,
      )}
    </>
  )
}
